import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { toast } from '@/hooks/use-toast'
import { AUTH_STORAGE_KEYS } from '@/types/auth'

/**
 * Decode JWT token payload without verification
 * Returns null if token is invalid
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/**
 * Check if the current auth token is valid and not expired
 * Returns object with validity status and time until expiry
 */
export function checkTokenValidity(): {
  isValid: boolean
  isExpired: boolean
  expiresInSeconds: number | null
  needsRefresh: boolean
} {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    || localStorage.getItem('auth0_access_token')

  if (!token) {
    return { isValid: false, isExpired: true, expiresInSeconds: null, needsRefresh: true }
  }

  const payload = decodeJwtPayload(token)
  if (!payload || !payload.exp) {
    // Token exists but can't decode - assume valid (non-JWT token)
    return { isValid: true, isExpired: false, expiresInSeconds: null, needsRefresh: false }
  }

  const exp = payload.exp as number
  const now = Math.floor(Date.now() / 1000)
  const expiresInSeconds = exp - now

  // Token is expired
  if (expiresInSeconds <= 0) {
    return { isValid: false, isExpired: true, expiresInSeconds: 0, needsRefresh: true }
  }

  // Token expires in less than 5 minutes - needs refresh
  const needsRefresh = expiresInSeconds < 300

  return {
    isValid: true,
    isExpired: false,
    expiresInSeconds,
    needsRefresh,
  }
}

/**
 * Estimate if token will remain valid for the duration of an upload
 * @param recordCount Total number of records to upload
 * @param batchSize Records per batch
 * @param delayMs Delay between batches in ms
 * @returns Whether the token will likely remain valid
 */
export function willTokenSurviveUpload(
  recordCount: number,
  batchSize: number = 100,
  delayMs: number = 500
): { willSurvive: boolean; estimatedSeconds: number; tokenExpiresIn: number | null } {
  const batchCount = Math.ceil(recordCount / batchSize)
  // Estimate: 2 seconds per batch for API call + delay between batches
  const estimatedSeconds = (batchCount * 2) + (batchCount * delayMs / 1000)

  const validity = checkTokenValidity()

  if (!validity.isValid || validity.expiresInSeconds === null) {
    return { willSurvive: false, estimatedSeconds, tokenExpiresIn: validity.expiresInSeconds }
  }

  // Add 60 second buffer
  const willSurvive = validity.expiresInSeconds > (estimatedSeconds + 60)

  return { willSurvive, estimatedSeconds, tokenExpiresIn: validity.expiresInSeconds }
}

// Create axios instance with relative URL - inherits protocol from page
const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: Error) => void
}> = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available (check both legacy and Auth0 token keys)
    const legacyToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
    const auth0Token = localStorage.getItem('auth0_access_token')
    const token = legacyToken || auth0Token

    // Debug logging for bulk upload requests
    // Performance metrics bulk upload requests are handled normally

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor with token refresh
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }
    const { response } = error
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)

    // Handle 401 Unauthorized
    if (response?.status === 401) {
      // Skip refresh for dev/demo tokens in development
      const isDevelopment =
        import.meta.env?.MODE === 'development' ||
        process.env.NODE_ENV === 'development'
      if (isDevelopment && (token?.startsWith('demo-token-') || token?.startsWith('dev-token-'))) {
        return Promise.reject(error)
      }

      // Don't retry if this is a login/refresh request
      const isAuthEndpoint =
        originalRequest?.url?.includes('/auth/login') ||
        originalRequest?.url?.includes('/auth/refresh') ||
        originalRequest?.url?.includes('/auth/register')

      if (isAuthEndpoint) {
        return Promise.reject(error)
      }

      // For Auth0 tokens there is no silent refresh mechanism in this app,
      // so clear the expired token and redirect to /login immediately.
      const auth0Token = localStorage.getItem('auth0_access_token')
      if (auth0Token) {
        localStorage.removeItem('auth0_access_token')
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      // Try to refresh token
      if (!originalRequest._retry) {
        if (isRefreshing) {
          // Wait for the refresh to complete
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`
                }
                resolve(api(originalRequest))
              },
              reject: (err: Error) => {
                reject(err)
              },
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)

        if (refreshToken) {
          try {
            const refreshResponse = await axios.post(
              '/api/v1/auth/refresh',
              { refresh_token: refreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            )

            const { access_token, refresh_token, expires_in } = refreshResponse.data

            // Store new tokens
            localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, access_token)
            localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refresh_token)
            localStorage.setItem(
              AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT,
              (Date.now() + expires_in * 1000).toString()
            )

            // Update default header
            api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

            // Process queued requests
            processQueue(null, access_token)

            // Retry original request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${access_token}`
            }
            return api(originalRequest)
          } catch (refreshError) {
            processQueue(refreshError as Error, null)
            // Refresh failed - clear tokens and redirect
            clearAuthAndRedirect()
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        } else {
          // No refresh token - clear and redirect
          clearAuthAndRedirect()
          return Promise.reject(error)
        }
      }

      // Retry already attempted - redirect to login
      clearAuthAndRedirect()
      return Promise.reject(error)
    }

    // Handle other errors
    if (response?.status === 403) {
      toast({ title: 'You do not have permission to perform this action', variant: 'destructive' })
    } else if (response?.status === 404) {
      // Don't show toast for 404 - let the component handle it
    } else if (response?.status === 422) {
      // Validation error - let the component handle it
    } else if (response?.status === 429) {
      toast({ title: 'Too many requests. Please try again later.', variant: 'destructive' })
    } else if (response?.status && response.status >= 500) {
      // If /auth/me returns 500, treat it as an auth failure rather than showing
      // a generic server error - the backend likely failed to verify the token
      const requestUrl = originalRequest?.url || ''
      if (requestUrl.includes('/auth/me')) {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }
      toast({ title: 'Server error. Please try again later.', variant: 'destructive' })
    } else if (error.code === 'ECONNABORTED') {
      toast({ title: 'Request timeout. Please check your connection.', variant: 'destructive' })
    } else if (!response) {
      toast({ title: 'Network error. Please check your connection.', variant: 'destructive' })
    }

    return Promise.reject(error)
  }
)

/**
 * Clear authentication and redirect to login
 */
function clearAuthAndRedirect() {
  // Never clear tokens or redirect in dev mode - the dev session is
  // entirely client-side and 401s from the backend are expected for
  // endpoints that have not yet been wired up for dev tokens.
  if (localStorage.getItem('dev_mode_auth')) {
    return
  }

  // Clear all auth storage
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT)

  // Clear headers
  delete api.defaults.headers.common['Authorization']

  // Only redirect if not already on auth pages
  const currentPath = window.location.pathname
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password']
  if (!authPaths.some((path) => currentPath.includes(path))) {
    window.location.href = '/login'
  }
}

export default api